import React, { useState } from "react";
import style from "./Details.module.scss";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";
import defaultEquipmentImage from "../../../../../../images/v2/equipnent_placeholder.png";
import config from "../../../../../config";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
const STRATUS_URL = config.paymentcloud.STRATUS_URL;

/**
 * Equipment component displays a list of terminal equipment and gateway information
 * @param {Object} props - Component props
 * @param {Object} props.appData - Application data containing equipment information
 * @param {Object} props.appData.ems_boot_equipments - Array of equipment data
 * @param {Object} props.appData.powerappequipment - Power app equipment data
 * @param {boolean} props.expandAppDetails - Flag to control expanded view
 * @returns {React.ReactElement} Equipment component
 */
function Equipments({ appData, expandAppDetails }) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery(768);

  /**
   * Gets the image URL for equipment
   * @param {string} imagePath - Path to the equipment image
   * @returns {string} Complete image URL or default image URL
   */
  const getEquipmentImage = (imagePath) => {
    if (!imagePath) return defaultEquipmentImage;
    return `${STRATUS_URL}get-image-by-path?path=${imagePath}`;
  };

  /**
   * Gets pricing type and associated class name for equipment
   * @param {Object} eqp - Equipment object
   * @returns {Object|null} Object containing pricing type and class name, or null if no pricing
   */
  const getPricingTypeWithClass = (eqp) => {
    if (eqp.purchase_for !== null) return { type: "Buy", className: style.buy };
    if (eqp.rent_for !== null) return { type: "Rent", className: style.rent };
    if (eqp.lease_for !== null) return { type: "Lease", className: style.lease };
    if (eqp.tpp !== null) return { type: "TPP", className: style.tpp };
    return null;
  };

  /**
   * Gets equipment name from terminal or gateway object
   * @param {Object} eqp - Equipment object
   * @returns {string} Equipment name
   */
  const getEquipmentName = (eqp) => {
    if (eqp.terminal?.equipment_name) return eqp.terminal.equipment_name;
    if (eqp.gateway?.equipment_name) return eqp.gateway.equipment_name;
    
    const name = eqp.terminal_name || eqp.gateway_name || "";
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  /**
   * Gets the price for an equipment item
   * @param {Object} eqp - Equipment object
   * @returns {number} The price for the equipment
   */
  const getEquipmentPrice = (eqp) => {
    if (eqp.purchase_for !== null) return eqp.purchase_for;
    if (eqp.rent_for !== null) return eqp.rent_for;
    if (eqp.lease_for !== null) return eqp.lease_for;
    if (eqp.tpp !== null) return eqp.tpp;
    return 0;
  };

  /**
   * Renders gateway section
   * @param {Object} equipment - Gateway equipment data
   * @param {boolean} padLeft - Whether to add left padding
   * @returns {React.ReactElement|null} Gateway section or null if no equipment
   */
  const renderGatewaySection = (equipment, padLeft = false) => {
    if (!equipment) return null;
    
    return (
      <div className={`${style.gateway_section} ${padLeft && style.pad_left}`}>
        <div className={style.gateway_label}>
          <span>GATEWAY</span>
        </div>
        <div className={style.gateway_item}>
          <div className={style.gateway_icon}>
            <img
              src={getEquipmentImage(equipment?.gateway?.equipment_image)}
              alt={getEquipmentName(equipment)}
              onError={(e) => {
                e.target.src = defaultEquipmentImage;
              }}
            />
          </div>
          <div className={style.gateway_details}>
            <h3>{getEquipmentName(equipment)}</h3>
            <p>ID {equipment?.gateway_id || equipment?.id}</p>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders terminal section
   * @param {Object} equipment - Terminal equipment data
   * @returns {React.ReactElement|null} Terminal section or null if no equipment
   */
  const renderTerminalSection = (equipment) => {
    if (!equipment) return null;

    return (
      <div className={style.gateway_section}>
        <div className={style.gateway_label}>
          <span>TERMINAL</span>
        </div>
        <div className={style.equipment_list}>
          {renderEquipmentItem(equipment)}
        </div>
      </div>
    );
  };

  /**
   * Renders individual equipment item
   * @param {Object} eqp - Equipment data
   * @returns {React.ReactElement|null} Equipment item or null if no equipment
   */
  const renderEquipmentItem = (eqp) => {
    if (!eqp) return null;

    const pricingType = getPricingTypeWithClass(eqp);
    const equipmentName = getEquipmentName(eqp);
    const price = getEquipmentPrice(eqp);

    return (
      <div className={style.equipment_item} key={eqp.id}>
        <img
          src={getEquipmentImage(eqp.terminal?.equipment_image)}
          alt={equipmentName}
          onError={(e) => {
            e.target.src = defaultEquipmentImage;
          }}
        />
        <div className={style.eqp_data}>
          <div className={style.eqp_heading}>
            <div className={style.name_and_badge}>
              <span className={style.eqp_name}>
                {equipmentName}
              </span>
              {pricingType && (
                <span className={`${style.badge} ${pricingType.className}`}>
                  {pricingType.type}
                </span>
              )}
            </div>
            <span className={style.quantity}>
              X {eqp.quantity || 1}
            </span>
          </div>
          <span className={style.desc}>
            $ {price.toFixed(2)}
          </span>
        </div>
      </div>
    );
  };

  /**
   * Renders list of equipment including terminals, add-ons, and gateways
   * @returns {React.ReactElement|null} Equipment list or null if no equipment
   */
  const renderEquipmentList = () => {
    if (!appData?.ems_boot_equipments?.length) return null;

    // Separate terminals and gateways
    const terminals = appData.ems_boot_equipments.filter(eqp => 
      eqp.equipment_option === "terminal"
    );
    
    const gateways = appData.ems_boot_equipments.filter(eqp => 
      eqp.equipment_option === "gateway"
    );

    return (
      <>
        {/* Render Terminal Equipment */}
        {terminals.map(renderEquipmentItem)}

        {/* Addon Section */}
        {appData.ems_boot_equipments.some(eqp => 
          eqp.terminal?.add_on_equipment_list?.length > 0 && 
          eqp.selected_addons?.length > 0
        ) && (
          <div className={style.data_item}>
            <p className={style.data_label}>Add-on</p>
            <p className={style.data_value}>
              {appData.ems_boot_equipments
                .filter(eqp => eqp.terminal?.add_on_equipment_list?.length > 0 && eqp.selected_addons?.length > 0)
                .map(eqp => eqp.terminal.add_on_equipment_list
                  .filter(addon => eqp.selected_addons.includes(addon.id))
                  .map(addon => addon.equipment_name)
                )
                .flat()
                .join(", ")}
            </p>
          </div>
        )}

        {/* Render Gateway Section */}
        {gateways.map(renderGatewaySection)}
      </>
    );
  };

  return (
    <div
      className={`${style.accordion_item} ${expandAppDetails ? style.expanded : ""}`}
    >
      <div
        className={`${style.accordion_head} ${isOpen ? style.open : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isMobile ? (
          <CaretRight24 className={`${style.caret_icon} ${isOpen ? style.expended : ""}`} />
        ) : (
          <CaretDown24 />
        )}
        <h5>Equipment</h5>
      </div>
      {isOpen && (
        <div className={`${style.accordion_body} ${isMobile && isOpen ? style.expanded : ""}`}>
          {appData?.billing && appData?.ems_boot_equipments?.length > 0 && (
            <div className={style.equipment_list}>
              {renderEquipmentList()}
            </div>
          )}

          {appData?.billing == null && appData?.powerappequipment && (
            <>
              {appData.powerappequipment.equipment_option === "gateway" ? (
                renderGatewaySection(appData.powerappequipment, 'gateway')
              ) : (
                renderTerminalSection(appData.powerappequipment)
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Equipments;
